import { useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import type { ListThreadsResponse } from "@onemileauto/shared-types";
import { moveThread } from "../lib/api-client";
import { mailboxQueryKeys } from "../lib/query-keys";

/**
 * Archive (or any folder move) with optimistic removal from the currently
 * viewed list — the thread disappears from the thread list immediately on
 * tap/swipe, rolling back if the request fails. Matches the architecture
 * plan's "Client does optimistic removal on swipe, rolling back on error."
 */
export function useMoveThread(mailboxId: string | undefined, folderSlug: string) {
  const queryClient = useQueryClient();
  const queryKey = mailboxQueryKeys.threads(mailboxId ?? "", folderSlug);

  return useMutation({
    mutationFn: ({ threadId, folderId }: { threadId: string; folderId: string }) =>
      moveThread(mailboxId!, threadId, folderId),
    onMutate: async ({ threadId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InfiniteData<ListThreadsResponse>>(queryKey);

      if (previous) {
        queryClient.setQueryData<InfiniteData<ListThreadsResponse>>(queryKey, {
          ...previous,
          pages: previous.pages.map((page) => ({
            ...page,
            threads: page.threads.filter((t) => t.id !== threadId),
          })),
        });
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      if (!mailboxId) return;
      // The thread landed in a different folder's list and its detail
      // view's folder context changed too — broad invalidation here is
      // simpler and safer than hand-tracking every affected cache entry.
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadAll(mailboxId) });
    },
  });
}
